import React, { useState, useEffect } from 'react';
import {
    Form,
    Input,
    Select,
    Row,
    Col,
    Button,
    Space,
    Modal,
} from 'antd';
import { apiRequest } from '../../../../service/apiRequest';
import { showSuccess, showError } from '../../../../components/errorHandler';

import { useAppDispatch } from '../../../../redux/hooks';
import { fetchEmployeeData } from '../../../../redux/slice/masterSlice';
const { TextArea } = Input;
const { Option } = Select;

interface DepartmentItem {
    department_id: number;
    department_name: string;
    department_status: number;
}

interface DesignationItem {
    designation_id: number;
    designation_name: string;
    designation_status: number;
}

const employeeTypes = [
    { label: 'Contractor', value: 'contractor' },
    { label: 'Company Employee', value: 'company employee' },
];

interface EmployeeModelProps {
    visible: boolean;
    onCancel: () => void;
    onCreate: (data: any) => void;
    initialValues?: {
        employee_id?: number;
        employee_code?: string;
        employee_fname?: string;
        employee_lname?: string;
        employee_mobile?: string;
        employee_department?: number;
        employee_type?: string;
        employee_salary?: number;
        employee_designation?: string;
        employee_address?: string;
    } | null;
    isEditMode?: boolean;
}

const EmployeeModel: React.FC<EmployeeModelProps> = ({
    visible,
    onCancel,
    onCreate,
    initialValues = null,
    isEditMode = false,
}) => {
    const dispatch = useAppDispatch();
    const [form] = Form.useForm();
    const [isModalClose, setIsModalClose] = useState(true);

    const [designations, setDesignation] = useState<DesignationItem[]>([]);
    const [departments, setDepartments] = useState<DepartmentItem[]>([]);

    const isSubmitDisabled = () => {
        const errors = form.getFieldsError();
        return errors.some(({ errors }) => errors.length > 0);
    };

    const generateEmployeeCode = async () => {
        try {
            const list = await apiRequest<any[]>({ method: 'get', url: '/employees' }).catch(() => []);
            let maxNum = 0;
            (list || []).forEach((emp: any) => {
                const code = String(emp.employee_code || '');
                if (code.toUpperCase().startsWith('SVICON')) {
                    const numPart = parseInt(code.replace(/[^0-9]/g, ''), 10);
                    if (!isNaN(numPart) && numPart > maxNum) {
                        maxNum = numPart;
                    }
                } else if (emp.employee_id && Number(emp.employee_id) > maxNum) {
                    maxNum = Number(emp.employee_id);
                }
            });
            const nextCode = `SVICON${String(maxNum + 1).padStart(4, '0')}`.slice(0, 15);
            return nextCode;
        } catch {
            return `SVICON0001`;
        }
    };

    // Submit handler
    const handleAddEmployee = async (values: any) => {
        try {
            const dataVal = {
                ...values,
                ...(initialValues?.employee_id && {
                    employee_id: initialValues.employee_id,
                }),
            };

            const url = isEditMode ? 'edit-employee' : 'add-employee';
            const method = isEditMode ? 'put' : 'post';

            const data = await apiRequest({
                method,
                url,
                data: dataVal,
            });

            onCreate(data);
            showSuccess(
                isEditMode
                    ? 'Employee updated successfully!'
                    : 'Employee created successfully!'
            );

            form.resetFields();

            if (isModalClose) {
                onCancel();
            } else {
                generateEmployeeCode().then((code) => {
                    form.setFieldsValue({ employee_code: code });
                });
            }
            dispatch(fetchEmployeeData());
        } catch (error: any) {
            showError(
                error?.response?.data?.message ||
                `Failed to ${isEditMode ? 'update' : 'create'} employee`
            );
        }
    };

    // Fetch designation list when component mounts
    useEffect(() => {
        const fetchDesignations = async () => {
            try {
                const designationData = await apiRequest<DesignationItem[]>({
                    method: 'get',
                    url: 'designation?status=1',
                });
                setDesignation(designationData);
            } catch (error) {
                showError('Failed to fetch designations');
            }
        };

        fetchDesignations();
    }, []);

    // Fetch from department master
    useEffect(() => {
        const fetchDepartments = async () => {
            try {
                const departmentData = await apiRequest<DepartmentItem[]>({
                    method: 'get',
                    url: 'departments?status=1',
                });
                setDepartments(departmentData);
            } catch (error) {
                showError('Failed to fetch departments');
            }
        };

        fetchDepartments();
    }, []);

    // Set initial values
    useEffect(() => {
        if (visible && isEditMode && initialValues) {
            let deptVal: any = initialValues.employee_department;
            if (typeof deptVal === 'string') {
                deptVal = deptVal.split(',').map(id => {
                    const parsed = parseInt(id.trim(), 10);
                    return isNaN(parsed) ? id.trim() : parsed;
                });
            } else if (typeof deptVal === 'number') {
                deptVal = [deptVal];
            }
            form.setFieldsValue({
                ...initialValues,
                employee_code: initialValues.employee_code || (initialValues.employee_id ? `SVICON${String(initialValues.employee_id).padStart(4, '0')}` : 'SVICON0001'),
                employee_department: deptVal || []
            });
        } else if (visible) {
            form.resetFields();
            generateEmployeeCode().then((code) => {
                form.setFieldsValue({ employee_code: code });
            });
        }
    }, [visible, isEditMode, initialValues, form]);

    return (
        <Modal
            title={isEditMode ? 'Edit Employee' : 'Add New Employee'}
            visible={visible}
            onCancel={onCancel}
            footer={null}
            width="90%"
            destroyOnHidden
            centered
            maskClosable={false}
        >
            <Form
                form={form}
                layout="vertical"
                onFinish={handleAddEmployee}
                autoComplete="off"
            >
                <Row gutter={16}>
                    {/* Emp Code */}
                    <Col span={8}>
                        <Form.Item
                            name="employee_code"
                            label="Emp Code"
                            rules={[
                                { max: 15, message: 'Emp Code cannot exceed 15 characters' },
                            ]}
                        >
                            <Input
                                maxLength={15}
                                readOnly={isEditMode}
                                placeholder="Auto generated Emp Code"
                                style={isEditMode ? { backgroundColor: '#f5f5f5', color: '#555' } : {}}
                            />
                        </Form.Item>
                    </Col>

                    {/* First Name */}
                    <Col span={8}>
                        <Form.Item
                            name="employee_fname"
                            label="First Name"
                            rules={[
                                { required: true, message: 'Enter first name' },
                            ]}
                            normalize={(value) => {
                                if (!value) return value;
                                let cleaned = value.replace(/[^a-zA-Z]/g, '');
                                if (cleaned.length > 0) {
                                    cleaned = cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
                                }
                                return cleaned;
                            }}
                        >
                            <Input
                                maxLength={50}
                                showCount
                                placeholder="Enter first name"
                            />
                        </Form.Item>
                    </Col>

                    {/* Last Name */}
                    <Col span={8}>
                        <Form.Item
                            name="employee_lname"
                            label="Last Name"
                            normalize={(value) => {
                                if (!value) return value;
                                let cleaned = value.replace(/[^a-zA-Z]/g, '');
                                if (cleaned.length > 0) {
                                    cleaned = cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
                                }
                                return cleaned;
                            }}
                        >
                            <Input
                                maxLength={50}
                                showCount
                                placeholder="Enter last name"
                            />
                        </Form.Item>
                    </Col>

                    {/* Mobile Number - Optional */}
                    <Col span={8}>
                        <Form.Item
                            name="employee_mobile"
                            label="Mobile Number"
                            rules={[
                                {
                                    validator: (_, value) => {
                                        if (!value) return Promise.resolve();
                                        if (!/^[0-9]{10}$/.test(value)) {
                                            return Promise.reject('Mobile number must be 10 digits');
                                        }
                                        return Promise.resolve();
                                    }
                                }
                            ]}
                            normalize={(value) => {
                                if (!value) return value;
                                return value.replace(/[^0-9]/g, '').slice(0, 10);
                            }}
                        >
                            <Input
                                maxLength={10}
                                showCount
                                type="tel"
                                placeholder="Enter mobile number (Optional)"
                            />
                        </Form.Item>
                    </Col>

                    {/* Department */}
                    <Col span={8}>
                        <div style={{ marginBottom: 8 }}>Department</div>

                        <Form.Item
                            name="employee_department"
                            rules={[{ required: true, message: 'Select at least one department!' }]}
                        >
                            <Select
                                mode="multiple"
                                showSearch
                                className="employee-dept-select"
                                placeholder="Select Department"
                                allowClear
                                style={{ width: '100%' }}
                                options={departments.map((d) => ({
                                    label: d.department_name,
                                    value: d.department_id,
                                }))}
                                filterOption={(input, option) =>
                                    (option?.label as string)
                                        .toLowerCase()
                                        .includes(input.toLowerCase())
                                }
                                dropdownMatchSelectWidth={false}
                            />
                        </Form.Item>
                    </Col>

                    {/* Type */}
                    <Col span={8}>
                        <Form.Item
                            name="employee_type"
                            label="Employee Type"
                            rules={[{ required: true, message: 'Select employee type' }]}
                        >
                            <Select placeholder="Select Type" showSearch>
                                {employeeTypes.map((type) => (
                                    <Option key={type.value} value={type.value}>
                                        {type.label}
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>

                    {/* Salary - Optional */}
                    <Col span={8}>
                        <Form.Item
                            name="employee_salary"
                            label="Salary"
                            normalize={(value) => {
                                if (value === undefined || value === null) return value;
                                let str = value.toString().replace(/[^0-9.]/g, '');
                                const parts = str.split('.');
                                if (parts.length > 2) {
                                    str = parts[0] + '.' + parts.slice(1).join('');
                                }
                                if (parts[1]?.length > 2) {
                                    str = parts[0] + '.' + parts[1].slice(0, 2);
                                }
                                if (parseFloat(str) > 99999999.99) {
                                    str = '99999999.99';
                                }
                                return str;
                            }}
                            rules={[
                                {
                                    validator: (_, value) => {
                                        if (value === undefined || value === null || value === '') {
                                            return Promise.resolve();
                                        }
                                        const num = parseFloat(value);
                                        if (isNaN(num)) {
                                            return Promise.reject('Salary must be a number');
                                        }
                                        if (num < 0) {
                                            return Promise.reject('Salary cannot be less than 0');
                                        }
                                        if (num > 99999999.99) {
                                            return Promise.reject('Salary cannot exceed 99,999,999.99');
                                        }
                                        if (!/^\d+(\.\d{0,2})?$/.test(value.toString())) {
                                            return Promise.reject('Salary can have up to 2 decimal places');
                                        }
                                        return Promise.resolve();
                                    },
                                },
                            ]}
                        >
                            <Input
                                placeholder="Salary (Optional)"
                                suffix={
                                    <span style={{ fontSize: '12px', color: '#888' }}>
                                        0 - 99,999,999.99
                                    </span>
                                }
                            />
                        </Form.Item>
                    </Col>

                    {/* Designation */}
                    <Col xs={24} sm={12} md={8}>
                        <div style={{ marginBottom: 8 }}>Designation</div>

                        <Form.Item
                            name="employee_designation"
                            rules={[{ required: true, message: 'Please select a designation!' }]}
                        >
                            <Select
                                showSearch
                                placeholder="Select Designation"
                                allowClear
                                style={{ width: '100%' }}
                                value={form.getFieldValue('employee_designation')}
                                onChange={(value) => {
                                    if (value) {
                                        form.setFieldsValue({
                                            employee_designation: value,
                                        });
                                    } else {
                                        form.setFieldsValue({ employee_designation: null });
                                    }
                                }}
                                options={[
                                    { label: 'Select Designation', value: null },
                                    ...designations.map((d) => ({
                                        label: d.designation_name,
                                        value: d.designation_id,
                                    })),
                                ]}
                                filterOption={(input, option) =>
                                    (option?.label as string).toLowerCase().includes(input.toLowerCase())
                                }
                                dropdownMatchSelectWidth={false}
                            />
                        </Form.Item>
                    </Col>

                    {/* Address with character count */}
                    <Col span={16}>
                        <Form.Item
                            name="employee_address"
                            label="Address"
                            rules={[{ required: true, message: 'Enter address' }]}
                        >
                            <TextArea
                                rows={3}
                                maxLength={200}
                                showCount
                                placeholder="Address"
                            />
                        </Form.Item>
                    </Col>
                </Row>

                <Form.Item style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Space>
                        <Button onClick={onCancel}>Cancel</Button>

                        {!isEditMode && (
                            <Button
                                type="primary"
                                htmlType="submit"
                                onClick={() => setIsModalClose(false)}
                                disabled={isSubmitDisabled()}
                            >
                                Submit & Create New
                            </Button>
                        )}

                        <Button
                            type="primary"
                            htmlType="submit"
                            onClick={() => setIsModalClose(true)}
                            disabled={isSubmitDisabled()}
                        >
                            Submit
                        </Button>
                    </Space>
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default EmployeeModel;
